import React, { useState, useMemo, useEffect } from "react";
import { AlertTriangle, Mail, Send, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { PreConsultTemplate } from "@/lib/settings/preConsultData";
import {
  Questionnaire,
  usePatienQuestionnaireData,
} from "@/lib/dashboard/questionnairesData";
import { useToast } from "@/hooks/use-toast";
import { useAllergiesState } from "@/lib/dashboard/allergiesData";
import { Input } from "@/components/ui/input";

interface SendQuestionnaireModalProps {
  isOpen: boolean;
  onClose: () => void;
  patientName: string;
  preConsultTemplates: PreConsultTemplate[];
  isDarkMode: boolean;
  onSendQuestionnaire: (
    questionnaire,
    message: string,
    subject: string,
    email: string,
  ) => void;
  patientEmail: string;
  setPatientEmail: (email: string) => void;
}

export function SendQuestionnaireModal({
  isOpen,
  onClose,
  patientName,
  preConsultTemplates,
  isDarkMode,
  onSendQuestionnaire,
  patientEmail,
  setPatientEmail,
}: SendQuestionnaireModalProps) {
  const { toast } = useToast();
  const [selectedTemplate, setSelectedTemplate] = useState<string>("");
  const [message, setMessage] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [subject, setSubject] = useState("");
  const { patientID } = useAllergiesState();
  const { getPatientQuestionnairesByPatientyId } = usePatienQuestionnaireData();
  const [localEmail, setLocalEmail] = useState("");

  useEffect(() => {
    if (isOpen) {
      setLocalEmail(patientEmail || "");
    }
  }, [isOpen, patientEmail]);

  // Filter active templates only
  const activeTemplates = useMemo(
    () => preConsultTemplates.filter((t) => t.active),
    [preConsultTemplates],
  );

  const selectedTemplateData = useMemo(
    () => activeTemplates.find((t) => t.id === selectedTemplate),
    [selectedTemplate, activeTemplates],
  );

  const handleSend = async () => {
    if (!selectedTemplate || !localEmail?.trim()) {
      toast({
        title: !selectedTemplate ? "Select a template" : "Email required",
        description: "Please select a questionnaire template to send.",
        variant: "destructive",
      });
      return;
    }

    if (!selectedTemplateData) {
      return;
    }

    setIsSending(true);

    try {
      onSendQuestionnaire(selectedTemplateData, message, subject, localEmail);

      setSelectedTemplate("");
      setMessage("");
      setSubject("");
      onClose();
      toast({
        title: "Questionnaire sent successfully",
        description: `"${selectedTemplateData.name}" has been emailed to ${localEmail}`,
      });
    } catch (error) {
      console.error("Error sending questionnaire:", error);
      toast({
        title: "Error sending questionnaire",
        description: "Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSending(false);
    }
  };

  const handleClose = () => {
    setSelectedTemplate("");
    setMessage("");
    setSubject("");
    setLocalEmail("");
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent
        className={`max-w-md ${
          isDarkMode ? "bg-gray-900 border-gray-700" : "bg-white"
        }`}
      >
        <DialogHeader>
          <DialogTitle
            className={`flex items-center gap-2 ${
              isDarkMode ? "text-white" : "text-gray-900"
            }`}
          >
            <Mail className="w-5 h-5" />
            Send Questionnaire
          </DialogTitle>
          <DialogDescription
            className={isDarkMode ? "text-gray-400" : "text-gray-600"}
          >
            Send a pre-consultation form to {patientName} at {patientEmail}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Warning Panel for Missing Email */}
          {(!localEmail || localEmail.trim() === "") && (
            <Alert
              variant="destructive"
              className={
                isDarkMode
                  ? "border-yellow-700/50 bg-yellow-900/20 text-yellow-300"
                  : "border-yellow-300 bg-yellow-50 text-yellow-800"
              }
            >
              <AlertTriangle className="w-4 h-4" />
              <AlertTitle>No Email Address Entered</AlertTitle>
              <AlertDescription>
                Please enter an email address below to send the questionnaire.
              </AlertDescription>
            </Alert>
          )}

          {/* Email Input Field */}
          <div className="space-y-2">
            <Label
              htmlFor="patient-email"
              className={isDarkMode ? "text-gray-200" : "text-gray-900"}
            >
              Patient Email Address
            </Label>
            <Input
              id="patient-email"
              type="email"
              placeholder="patient@example.com"
              value={localEmail}
              onChange={(e) => setLocalEmail(e.target.value)}
              className={
                isDarkMode ? "bg-gray-800 border-gray-700 text-white" : ""
              }
            />
          </div>

          {/* Template Selection */}
          <div className="space-y-2">
            <Label
              htmlFor="template-select"
              className={isDarkMode ? "text-gray-200" : "text-gray-900"}
            >
              Select Questionnaire Template
            </Label>
            {activeTemplates.length === 0 ? (
              <div
                className={`p-3 border rounded-lg text-sm ${
                  isDarkMode
                    ? "bg-gray-800 border-gray-700 text-gray-300"
                    : "bg-gray-50 border-gray-200 text-gray-600"
                }`}
              >
                No active templates available. Please create and activate a
                pre-consult template in settings first.
              </div>
            ) : (
              <>
                <Select
                  value={selectedTemplate}
                  onValueChange={setSelectedTemplate}
                >
                  <SelectTrigger
                    id="template-select"
                    className={
                      isDarkMode
                        ? "bg-gray-800 border-gray-700 text-white"
                        : "bg-white border-gray-200"
                    }
                  >
                    <SelectValue placeholder="Choose a template..." />
                  </SelectTrigger>
                  <SelectContent
                    className={`${
                      isDarkMode ? "bg-gray-800 border-gray-700" : "bg-white"
                    } w-[var(--radix-select-trigger-width)]`}
                  >
                    {activeTemplates.map((template) => (
                      <SelectItem
                        key={template.id}
                        value={template.id}
                        className="items-start whitespace-normal break-words"
                      >
                        {template.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {/* Template Preview */}
                {selectedTemplateData && (
                  <div
                    className={`p-3 border rounded-lg text-sm ${
                      isDarkMode
                        ? "bg-gray-800 border-gray-700"
                        : "bg-gray-50 border-gray-200"
                    }`}
                  >
                    <div
                      className={`font-medium mb-1 ${
                        isDarkMode ? "text-gray-200" : "text-gray-900"
                      }`}
                    >
                      {selectedTemplateData.name}
                    </div>
                    {selectedTemplateData.description && (
                      <div
                        className={`text-xs ${
                          isDarkMode ? "text-gray-400" : "text-gray-600"
                        }`}
                      >
                        {selectedTemplateData.description}
                      </div>
                    )}
                    <div
                      className={`text-xs mt-2 ${
                        isDarkMode ? "text-gray-500" : "text-gray-500"
                      }`}
                    >
                      {selectedTemplateData.questions?.length || 0} questions
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
          {/* Subject Field */}
          <div className="space-y-2">
            <Label
              htmlFor="subject"
              className={isDarkMode ? "text-gray-200" : "text-gray-900"}
            >
              Subject
            </Label>
            <input
              id="subject"
              type="text"
              placeholder="Enter email subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              maxLength={150}
              className={`w-full p-2 rounded-md border text-sm ${
                isDarkMode
                  ? "bg-gray-800 border-gray-700 text-white"
                  : "bg-white border-gray-300"
              }`}
            />
            <div
              className={`text-xs text-right ${
                isDarkMode ? "text-gray-400" : "text-gray-500"
              }`}
            >
              {subject.length}/150 characters
            </div>
          </div>

          {/* Optional Message */}
          <div className="space-y-2">
            <Label
              htmlFor="message"
              className={isDarkMode ? "text-gray-200" : "text-gray-900"}
            >
              Optional Message
            </Label>
            <Textarea
              id="message"
              placeholder="Add a personal message to include with the questionnaire..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={3}
              maxLength={500}
              className={
                isDarkMode ? "bg-gray-800 border-gray-700 text-white" : ""
              }
            />
            <div
              className={`text-xs text-right ${
                isDarkMode ? "text-gray-400" : "text-gray-500"
              }`}
            >
              {message.length}/500 characters
            </div>
          </div>

          {/* Info Box */}
          <div
            className={`p-3 border rounded-lg text-sm ${
              isDarkMode
                ? "bg-blue-900/20 border-blue-700/50 text-blue-300"
                : "bg-blue-50 border-blue-200 text-blue-700"
            }`}
          >
            <p className="mb-1 font-medium">What happens next:</p>
            <ul className="space-y-1 text-xs list-disc list-inside">
              <li>
                {patientName} Will receive an email with a link to the form
              </li>
              <li>The form will appear in their dashboard as "pending"</li>
              <li>
                Once completed, the responses will be available for review
              </li>
            </ul>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isSending}
            className={isDarkMode ? "border-gray-700" : ""}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSend}
            disabled={
              !selectedTemplate ||
              isSending ||
              activeTemplates.length === 0 ||
              !localEmail.trim()
            }
            className="bg-slate-900 hover:bg-slate-800"
          >
            {isSending ? (
              <>
                <div className="w-4 h-4 mr-2 border-2 border-white rounded-full border-t-transparent animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Send className="w-4 h-4 mr-2" />
                Send Questionnaire
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
